'use client'

import { Activity, TrendingUp, TrendingDown, Zap } from 'lucide-react'
import {
  Area,
  AreaChart,
  CartesianGrid,
  XAxis,
  YAxis,
  ReferenceLine,
  Dot,
} from 'recharts'
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
} from '@/components/ui/chart'
import { InstanceScaleHistoryItem } from '@/services/inference.gen'
import { useMemo } from 'react'
import dayjs from 'dayjs'

const chartConfig = {
  replicas: {
    label: 'Replicas',
    color: 'hsl(var(--primary))',
    icon: Activity,
  },
} satisfies ChartConfig

type ScaleHistoryChartProps = {
  data: InstanceScaleHistoryItem[]
  timeRange: string
}

type ScaleEvent = {
  timestamp: number
  previousValue: number
  newValue: number
  type: 'scale_up' | 'scale_down'
}

type ChartStats = {
  average: number
  max: number
  min: number
  totalScaleEvents: number
  upscaleEvents: number
  downscaleEvents: number
}

export function ScaleHistoryChart({ data, timeRange }: ScaleHistoryChartProps) {
  const { chartData, scaleEvents, stats } = useMemo(() => {
    if (!data || data.length === 0) {
      return {
        chartData: [],
        scaleEvents: [],
        stats: {
          average: 0,
          max: 0,
          min: 0,
          totalScaleEvents: 0,
          upscaleEvents: 0,
          downscaleEvents: 0,
        },
      }
    }

    const formatString =
      timeRange === '1h' ? 'h:mm A' : timeRange === '24h' ? 'h A' : 'MMM D, h A'

    const sortedData = [...data].sort((a, b) => a.ts - b.ts)
    const chartData = sortedData.map((item) => ({
      value: item.value,
      time: dayjs.unix(item.ts).format(formatString),
      timestamp: item.ts,
    }))

    // Calculate scale events
    const scaleEvents: ScaleEvent[] = []
    for (let i = 1; i < sortedData.length; i++) {
      const current = sortedData[i]
      const previous = sortedData[i - 1]

      if (current.value !== previous.value) {
        scaleEvents.push({
          timestamp: current.ts,
          previousValue: previous.value,
          newValue: current.value,
          type: current.value > previous.value ? 'scale_up' : 'scale_down',
        })
      }
    }

    // Calculate stats
    const values = sortedData.map((item) => item.value)
    const average = values.reduce((sum, val) => sum + val, 0) / values.length
    const upscaleEvents = scaleEvents.filter(
      (e) => e.type === 'scale_up'
    ).length
    const downscaleEvents = scaleEvents.filter(
      (e) => e.type === 'scale_down'
    ).length

    const stats: ChartStats = {
      average: Math.round(average * 10) / 10,
      max: Math.max(...values),
      min: Math.min(...values),
      totalScaleEvents: scaleEvents.length,
      upscaleEvents,
      downscaleEvents,
    }

    return { chartData, scaleEvents, stats }
  }, [data, timeRange])

  if (!data?.length)
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <Activity className="h-12 w-12 text-muted-foreground/50 mb-4" />
        <h4 className="text-lg font-medium text-foreground mb-2">
          No Data Available
        </h4>
        <p className="text-sm text-muted-foreground max-w-md">
          No scaling history is available for the selected time range.
        </p>
      </div>
    )

  const CustomTooltip = ({
    active,
    payload,
  }: {
    active?: boolean
    payload?: Array<{ payload: { value: number; timestamp: number } }>
  }) => {
    if (!active || !payload?.[0]) return null
    const data = payload[0].payload
    const scaleEvent = scaleEvents.find(
      (e) => Math.abs(e.timestamp - data.timestamp) < 300
    )

    return (
      <div className="bg-background/95 backdrop-blur-sm border rounded-lg shadow-lg p-4 min-w-[200px]">
        <div className="flex items-center gap-2 mb-2">
          <Activity className="h-4 w-4 text-primary" />
          <span className="font-medium">{data.value} Replicas</span>
        </div>
        <div className="text-sm text-muted-foreground mb-2">
          {dayjs.unix(data.timestamp).format('MMM D, YYYY h:mm A')}
        </div>
        {scaleEvent && (
          <div className="pt-2 border-t">
            <div className="flex items-center gap-2 text-sm">
              {scaleEvent.type === 'scale_up' ? (
                <TrendingUp className="h-3 w-3 text-green-500" />
              ) : (
                <TrendingDown className="h-3 w-3 text-orange-500" />
              )}
              <span className="text-xs text-muted-foreground">
                Scaled {scaleEvent.type === 'scale_up' ? 'up' : 'down'} from{' '}
                {scaleEvent.previousValue} → {scaleEvent.newValue}
              </span>
            </div>
          </div>
        )}
      </div>
    )
  }

  const getDynamicYDomain = () => [
    0,
    stats.max <= 5
      ? 5
      : stats.max <= 10
        ? 10
        : stats.max <= 20
          ? 20
          : Math.ceil(stats.max * 1.1),
  ]

  return (
    <div className="space-y-6">
      <div className="bg-card/50 rounded-md p-3 border flex items-center gap-6">
        <div className="flex items-center gap-2">
          <Activity className="h-3.5 w-3.5 text-chart-2" />
          <span className="text-xs font-medium">Avg:</span>
          <span className="text-sm font-bold">{stats.average}</span>
          <span className="text-xs text-muted-foreground">
            over {timeRange}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Zap className="h-3.5 w-3.5 text-chart-3" />
          <span className="text-xs font-medium">Events:</span>
          <span className="text-sm font-bold">{stats.totalScaleEvents}</span>
          <div className="flex gap-2 text-xs">
            <span className="flex items-center gap-0.5 text-green-600">
              <TrendingUp className="h-2.5 w-2.5" />
              {stats.upscaleEvents}
            </span>
            <span className="flex items-center gap-0.5 text-orange-600">
              <TrendingDown className="h-2.5 w-2.5" />
              {stats.downscaleEvents}
            </span>
          </div>
        </div>
      </div>

      <div className="relative">
        <ChartContainer config={chartConfig} className="h-[300px] w-full">
          <AreaChart
            data={chartData}
            margin={{ top: 20, right: 20, bottom: 20, left: 20 }}
          >
            <defs>
              <linearGradient id="replicaGradient" x1="0" y1="0" x2="0" y2="1">
                <stop
                  offset="0%"
                  stopColor="hsl(var(--primary))"
                  stopOpacity={0.8}
                />
                <stop
                  offset="50%"
                  stopColor="hsl(var(--primary))"
                  stopOpacity={0.3}
                />
                <stop
                  offset="100%"
                  stopColor="hsl(var(--primary))"
                  stopOpacity={0.05}
                />
              </linearGradient>
            </defs>

            <CartesianGrid
              strokeDasharray="3 3"
              stroke="hsl(var(--border))"
              vertical={false}
              opacity={0.3}
            />

            <XAxis
              dataKey="time"
              tickLine={false}
              axisLine={false}
              tickMargin={12}
              className="text-xs fill-muted-foreground"
              minTickGap={30}
            />

            <YAxis
              domain={getDynamicYDomain()}
              tickLine={false}
              axisLine={false}
              tickMargin={12}
              className="text-xs fill-muted-foreground"
              tickFormatter={(value) => `${value}`}
            />

            {stats.max > stats.min && (
              <>
                <ReferenceLine
                  y={stats.max}
                  stroke="hsl(var(--chart-3))"
                  strokeDasharray="2 2"
                  opacity={0.5}
                />
                <ReferenceLine
                  y={stats.min}
                  stroke="hsl(var(--chart-4))"
                  strokeDasharray="2 2"
                  opacity={0.5}
                />
              </>
            )}

            <ChartTooltip
              content={<CustomTooltip />}
              cursor={{
                stroke: 'hsl(var(--primary))',
                strokeWidth: 1,
                strokeDasharray: '4 4',
              }}
            />

            <Area
              dataKey="value"
              type="stepAfter"
              fill="url(#replicaGradient)"
              stroke="hsl(var(--primary))"
              strokeWidth={2}
              connectNulls={false}
            />

            {scaleEvents.map((event, index) =>
              chartData.find(
                (d) => Math.abs(d.timestamp - event.timestamp) < 300
              ) ? (
                <Dot
                  key={index}
                  cx={0}
                  cy={0}
                  r={4}
                  fill={
                    event.type === 'scale_up'
                      ? 'hsl(var(--chart-3))'
                      : 'hsl(var(--chart-4))'
                  }
                  stroke="hsl(var(--background))"
                  strokeWidth={2}
                  className="animate-pulse"
                />
              ) : null
            )}
          </AreaChart>
        </ChartContainer>
      </div>
    </div>
  )
}
